import { useState, useEffect, useRef } from 'react';
import OrderTimeline from './OrderTimeline';
import CustomerRouteMap from '@/components/feature/CustomerRouteMap';
import { supabase } from '@/lib/supabase';
import type { CustomerNotification } from '@/lib/supabase';

type DisplayStatus = 'delivered' | 'in-transit' | 'cancelled' | 'out-for-delivery' | 'pending' | 'confirmed';
type RawStatus = 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';

interface OrderItem {
  name: string;
  image: string;
  size?: string;
  quantity?: number;
}

interface OrderHistoryCardProps {
  id: string;
  date: string;
  updatedAt?: string;
  items: OrderItem[];
  total: number;
  status: DisplayStatus;
  rawStatus: RawStatus;
  deliveryOtp?: string;
  otpVerified?: boolean;
  notifications: CustomerNotification[];
  isLatest?: boolean;
  onTrack: () => void;
  onViewBill: () => void;
}

const STATUS_CONFIG: Record<DisplayStatus, { label: string; color: string; bg: string; border: string; icon: string; dot: string }> = {
  pending: {
    label: 'Pending',
    color: 'text-gray-600',
    bg: 'bg-gray-100',
    border: 'border-gray-200',
    icon: 'ri-time-line',
    dot: 'bg-gray-400',
  },
  confirmed: {
    label: 'Confirmed',
    color: 'text-sky-700',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    icon: 'ri-checkbox-circle-line',
    dot: 'bg-sky-500',
  },
  'in-transit': {
    label: 'In Transit',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'ri-truck-line',
    dot: 'bg-amber-500',
  },
  'out-for-delivery': {
    label: 'Out for Delivery',
    color: 'text-orange-700',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: 'ri-e-bike-2-fill',
    dot: 'bg-orange-500',
  },
  delivered: {
    label: 'Delivered',
    color: 'text-green-700',
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'ri-checkbox-circle-fill',
    dot: 'bg-green-500',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'ri-close-circle-line',
    dot: 'bg-red-500',
  },
};

function CancelReason({ orderId, notifications }: { orderId: string; notifications: CustomerNotification[] }) {
  const cancelNotif = notifications.find(
    n => n.type === 'order_cancelled' && orderId.toUpperCase() === n.order_id.toUpperCase()
  );
  if (!cancelNotif) return null;

  let reason = '';
  const match = cancelNotif.message.match(/Reason:\s*(.+?)\.?\s*We apologize/i);
  if (match) reason = match[1].trim();
  else {
    const parts = cancelNotif.message.split('Reason:');
    if (parts[1]) reason = parts[1].replace(/We apologize.*/i, '').trim().replace(/\.$/, '');
  }

  if (!reason) return null;

  return (
    <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-red-50 border border-red-200 mt-2">
      <i className="ri-information-line text-red-400 text-sm shrink-0 mt-0.5"></i>
      <div>
        <p className="text-red-600 text-[11px] font-bold">Cancellation Reason</p>
        <p className="text-red-500 text-xs mt-0.5 leading-relaxed">{reason}</p>
      </div>
    </div>
  );
}

export default function OrderHistoryCard({
  id,
  date,
  updatedAt,
  items,
  total,
  status,
  rawStatus,
  deliveryOtp,
  otpVerified,
  notifications,
  isLatest,
  onTrack,
  onViewBill,
}: OrderHistoryCardProps) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[status];
  const isActive = status === 'out-for-delivery' || status === 'confirmed' || status === 'pending';
  const isCancelled = status === 'cancelled';
  const isDelivered = status === 'delivered';
  const isOutForDelivery = status === 'out-for-delivery';

  // Live delivery location state
  const [deliveryLat, setDeliveryLat] = useState<number | null>(null);
  const [deliveryLng, setDeliveryLng] = useState<number | null>(null);
  const [deliveryBoyName, setDeliveryBoyName] = useState('Delivery Partner');
  const [customerLat, setCustomerLat] = useState<number | null>(null);
  const [customerLng, setCustomerLng] = useState<number | null>(null);
  const [customerAddress, setCustomerAddress] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Load customer coords from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('userAddress');
      if (saved) {
        const p = JSON.parse(saved);
        if (p.lat) setCustomerLat(Number(p.lat));
        if (p.lng) setCustomerLng(Number(p.lng));
        setCustomerAddress(`${p.address || ''}, ${p.city || ''}`.trim().replace(/^,|,$/g, ''));
      }
    } catch { /* ignore */ }
  }, []);

  // Subscribe to live delivery location when out-for-delivery
  useEffect(() => {
    if (!isOutForDelivery) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return;
    }

    // Initial fetch
    supabase
      .from('orders')
      .select('delivery_lat,delivery_lng,delivery_boy_name,customer_lat,customer_lng,customer_address,customer_city')
      .eq('id', id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          if (data.delivery_lat) setDeliveryLat(data.delivery_lat);
          if (data.delivery_lng) setDeliveryLng(data.delivery_lng);
          if (data.delivery_boy_name) setDeliveryBoyName(data.delivery_boy_name);
          if (data.customer_lat && !customerLat) setCustomerLat(data.customer_lat);
          if (data.customer_lng && !customerLng) setCustomerLng(data.customer_lng);
          if (data.delivery_lat) setLastUpdated(new Date());
        }
      });

    // Realtime subscription
    const channel = supabase
      .channel(`card-tracking-${id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders', filter: `id=eq.${id}` },
        (payload) => {
          const u = payload.new as { delivery_lat?: number; delivery_lng?: number; delivery_boy_name?: string };
          if (u.delivery_lat) { setDeliveryLat(u.delivery_lat); setLastUpdated(new Date()); }
          if (u.delivery_lng) setDeliveryLng(u.delivery_lng);
          if (u.delivery_boy_name) setDeliveryBoyName(u.delivery_boy_name);
        }
      )
      .subscribe();

    channelRef.current = channel;
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isOutForDelivery, id, customerLat, customerLng]);

  const formattedDate = new Date(date).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  const formattedTime = new Date(date).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all duration-300 ${
      isCancelled
        ? 'border-red-200 bg-red-50/30'
        : isDelivered
        ? 'border-green-200 bg-green-50/20'
        : isLatest
        ? 'border-orange-300 bg-orange-50/30'
        : 'border-gray-200 bg-white'
    }`}>
      {/* Top accent bar */}
      <div className={`h-1 w-full ${
        isCancelled ? 'bg-red-400' :
        isDelivered ? 'bg-green-400' :
        status === 'out-for-delivery' ? 'bg-gradient-to-r from-orange-400 to-red-400' :
        status === 'confirmed' ? 'bg-sky-400' :
        'bg-gray-300'
      }`}></div>

      {/* Main card content */}
      <div className="p-4">
        {/* Row 1: Order ID + Status badge + Latest tag */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {isLatest && (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-orange-500 text-white uppercase tracking-wide">
                  Latest
                </span>
              )}
              <span className="text-gray-900 font-bold text-sm truncate">{id}</span>
            </div>
            <p className="text-gray-400 text-[11px] mt-0.5 flex items-center gap-1">
              <i className="ri-calendar-2-line text-[10px]"></i>
              {formattedDate}
              <span className="text-gray-300">·</span>
              <i className="ri-time-line text-[10px]"></i>
              {formattedTime}
            </p>
          </div>

          {/* Status badge */}
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border shrink-0 ${cfg.bg} ${cfg.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot} ${isActive ? 'animate-pulse' : ''}`}></span>
            <i className={`${cfg.icon} ${cfg.color} text-xs`}></i>
            <span className={`text-xs font-bold whitespace-nowrap ${cfg.color}`}>{cfg.label}</span>
          </div>
        </div>

        {/* Row 2: Items preview */}
        <div className="flex items-center gap-2 mb-3">
          {/* Item images */}
          <div className="flex -space-x-2 shrink-0">
            {items.slice(0, 3).map((item, idx) => (
              <div
                key={idx}
                className="w-10 h-10 rounded-xl border-2 border-white overflow-hidden bg-gray-100 shrink-0"
                style={{ zIndex: 3 - idx }}
              >
                {item.image ? (
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-t-shirt-2-line text-gray-400 text-sm"></i>
                  </div>
                )}
              </div>
            ))}
            {items.length > 3 && (
              <div className="w-10 h-10 rounded-xl border-2 border-white bg-gray-100 flex items-center justify-center shrink-0" style={{ zIndex: 0 }}>
                <span className="text-gray-500 text-[10px] font-bold">+{items.length - 3}</span>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-800 text-xs font-semibold truncate">
              {items.map(i => i.name).join(', ')}
            </p>
            <p className="text-gray-400 text-[11px] mt-0.5">
              {items.length} item{items.length > 1 ? 's' : ''}
              {items[0]?.size && ` · Size: ${items[0].size}`}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-gray-900 font-black text-base leading-tight">₹{total.toLocaleString('en-IN')}</p>
            <p className="text-gray-400 text-[10px]">Total</p>
          </div>
        </div>

        {/* Compact timeline — always visible */}
        <div className="mb-3">
          <OrderTimeline
            status={rawStatus}
            createdAt={date}
            updatedAt={updatedAt || date}
            compact
          />
        </div>

        {/* ── LIVE DELIVERY MAP — only when out-for-delivery ── */}
        {isOutForDelivery && (
          <div className="mb-3 rounded-xl overflow-hidden border border-orange-200">
            {/* Live map header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center">
                  <i className="ri-e-bike-2-fill text-white text-xs"></i>
                </div>
                <p className="text-white font-bold text-xs">Delivery Boy Live Location</p>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
                <span className="text-white/90 text-[10px] font-bold">LIVE</span>
              </div>
            </div>
            <CustomerRouteMap
              deliveryLat={deliveryLat}
              deliveryLng={deliveryLng}
              customerLat={customerLat}
              customerLng={customerLng}
              deliveryBoyName={deliveryBoyName}
              customerAddress={customerAddress}
              isLive={!!(deliveryLat && deliveryLng)}
              lastUpdated={lastUpdated}
            />
          </div>
        )}

        {/* Cancel reason */}
        {isCancelled && (
          <CancelReason orderId={id} notifications={notifications} />
        )}

        {/* OTP badge */}
        {deliveryOtp && rawStatus !== 'delivered' && rawStatus !== 'cancelled' && !otpVerified && (
          <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-300 mt-2">
            <i className="ri-shield-keyhole-fill text-amber-500 text-base shrink-0"></i>
            <div className="flex-1 min-w-0">
              <p className="text-amber-600 text-[10px] font-black uppercase tracking-wide">Delivery OTP</p>
              <p className="font-mono font-black text-amber-900 text-xl tracking-[0.25em] leading-tight">{deliveryOtp}</p>
            </div>
            <p className="text-amber-400 text-[9px] leading-tight max-w-[60px] text-right">Share only at delivery</p>
          </div>
        )}

        {/* Action buttons row */}
        <div className="flex items-center gap-2 mt-3">
          {status === 'out-for-delivery' ? (
            <button
              onClick={onTrack}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold cursor-pointer whitespace-nowrap hover:shadow-md transition-all"
            >
              <i className="ri-map-pin-2-line"></i>
              Full Screen Track
            </button>
          ) : isDelivered ? (
            <button
              onClick={onViewBill}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 text-xs font-bold cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-file-list-3-line"></i>
              View Bill
            </button>
          ) : (
            <div className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-400 text-xs font-semibold">
              <i className="ri-time-line"></i>
              {isCancelled ? 'Order Cancelled' : 'Bill after delivery'}
            </div>
          )}

          {/* Expand/collapse timeline */}
          <button
            onClick={() => setExpanded(p => !p)}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap transition-all border ${
              expanded
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
            }`}
          >
            <i className={`ri-route-line`}></i>
            Timeline
            <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
          </button>
        </div>
      </div>

      {/* Expanded full timeline */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4 bg-white/80">
          <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-4">Order Journey</p>
          <OrderTimeline
            status={rawStatus}
            createdAt={date}
            updatedAt={updatedAt || date}
            compact={false}
          />
        </div>
      )}
    </div>
  );
}
